import type { Queryfilter as QueryFilter } from '@gonfalon/queryfilter/builder';
import {
  and,
  anyOf,
  contains,
  equals,
  notContains,
  notEquals,
  toString as queryfilterToString,
} from '@gonfalon/queryfilter/builder';

import type { GetMetricsOpenAPIQueryParams } from '../generated/operations/raw-openapi-query-params-for-serializers';

export type GetMetricsQueryParams = Omit<GetMetricsOpenAPIQueryParams, 'expand' | 'sort' | 'filter'> & {
  expand?: Array<
    | 'experiments'
    | 'experimentCount'
    | 'guardedRolloutCount'
    | 'metricGroupCount'
    | 'lastUsedInEntity'
    | 'releasePolicyCount'
  >;
  query?: string;
  sort?: string;
  filter?: {
    query?: string;
    eventKind?: string;
    isNumeric?: boolean;
    unitAggregationType?: 'sum' | 'average' | 'count_distinct';
    tags?: string[];
    tagsNotContains?: string[];
    dataSourceKeys?: string[];
    dataSourceKeysNotEquals?: string[];
    maintainerIds?: string[];
    maintainerTeamKey?: string;
    hasConnections?: string | boolean | undefined | null;
    view?: string;
    archived?: string | boolean | undefined | null;
    usedIn?: 'experiments' | 'guarded_rollouts' | 'release_policies' | 'any' | 'none';
    eventKeys?: string[];
    versionIds?: string[];
  };
};

export function querySerializer(queryParams: GetMetricsQueryParams) {
  const searchParams = new URLSearchParams();

  if (queryParams.expand) {
    searchParams.set('expand', queryParams.expand.join(','));
  }

  if (queryParams.filter) {
    const serializedFilter = serializeMetricFilterParam(queryParams.filter);
    if (serializedFilter) {
      searchParams.set('filter', serializedFilter);
    }
  }

  if (queryParams.query) {
    searchParams.set('q', queryParams.query);
  }

  if (queryParams.limit) {
    searchParams.set('limit', queryParams.limit.toString());
  }

  if (queryParams.offset) {
    searchParams.set('offset', queryParams.offset.toString());
  }

  if (queryParams.sort) {
    searchParams.set('sort', queryParams.sort);
  }

  return searchParams.toString();
}

export function serializeMetricFilterParam(value: NonNullable<GetMetricsQueryParams['filter']>) {
  const filter: QueryFilter[] = [];

  if (value.query) {
    filter.push(equals('query', value.query));
  }

  if (value.eventKind) {
    filter.push(equals('eventKind', value.eventKind));
  }

  if (value.isNumeric !== undefined) {
    filter.push(equals('isNumeric', value.isNumeric));
  }

  if (value.unitAggregationType) {
    filter.push(equals('unitAggregationType', value.unitAggregationType));
  }

  if (value.tags) {
    filter.push(contains('tags', value.tags));
  }

  if (value.tagsNotContains) {
    filter.push(notContains('tags', value.tagsNotContains));
  }

  if (value.dataSourceKeys) {
    filter.push(anyOf('dataSourceKeys', value.dataSourceKeys));
  }

  if (value.dataSourceKeysNotEquals) {
    filter.push(notEquals('dataSourceKeys', value.dataSourceKeysNotEquals));
  }

  if (value.maintainerIds) {
    filter.push(anyOf('maintainerIds', value.maintainerIds));
  }

  if (value.maintainerTeamKey) {
    filter.push(equals('maintainerTeamKey', value.maintainerTeamKey));
  }

  if (value.hasConnections !== undefined) {
    filter.push(equals('hasConnections', value.hasConnections));
  }

  if (value.view) {
    filter.push(equals('view', value.view));
  }

  if (value.archived === true || value.archived === 'true') {
    filter.push(equals('lifecycleState', 'archived'));
  }

  if (value.usedIn) {
    filter.push(equals('metricUsedIn', value.usedIn));
  }

  if (value.eventKeys?.length) {
    filter.push(anyOf('eventKeys', value.eventKeys));
  }

  if (value.versionIds?.length) {
    filter.push(anyOf('versionIds', value.versionIds));
  }

  return queryfilterToString(and(filter));
}
